import waproxyClient from '../utils/waproxy-calls.js';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import YAML from 'yamljs';

// Constants pour les types de Main Class
export const MAIN_CLASS = {
  MANUSCRIPT_PART: 'ManuscriptPart',
  ARTICLE: 'Article',
  BLOCK: 'Statblock'
};

// Constants pour les rôles des waItem
// NOTA: Les valeurs correspondent aux données stockées en base (table tab_actions, colonne needs)
export const WA_ITEM_ROLE = {
  MAIN: 'Main',
  CHILD: 'Child',
  RELATED: 'Article',
  PARENT: 'Parent',
  WORLDMETA: '*WorldMeta*',
  NONE: 'Nothing'
};

// Constantes pour les axes de recherche
export const PATH_AXIS = {
  CHILDREN: 'children',
  PARENT: 'parent',
  PREVIOUS: 'previous',
  PRECEDING_SIBLING: 'precedingSibling',
  FOLLOWING_SIBLING: 'followingSibling'
};

// ID par défaut en cas d'erreur
export const ERROR_ID = '00000000-0000-0000-0000-000000000000';
export const ERROR_DATA = {
  "id": ERROR_ID,
  "title": ""
};

// Classe waItem pour gérer les données World Anvil
export class WaItem {
  constructor(role, isMain = false) {
    this.role = role;
    this.isMain = isMain;
    this.mainClass = null;
    this.id = ERROR_ID;
    this.data = { ...ERROR_DATA };
    this.axisData = {};
    this.isDirty = false; // Needs refresh
    this.belTagNames = [];
    this.belTags = [];
    this.otherTagNames = [];
    this.isProtected = false;
    this.blockKeys = [];
  }

  // Fonction getData pour charger les données selon le Main Class
  async getData(id, mainClass) {
    // Le waItem Main ne peut pas utiliser getData directement
    if (this.isMain) {
      const errorMsg = 'waItem Main ne peut pas utiliser getData directement. Utilisez refreshData().';
      debug.error(config, 'WaItem.getData:', errorMsg);
      throw new Error(errorMsg);
    }

    try {
      // Déterminer l'endpoint et la granularité selon le type
      let endpoint, granularity;
      
      // Vérifier si l'id correspond au wa_world.id du localStorage
      let waWorldId = null;
      const waWorldString = localStorage.getItem('wa_world');
      if (waWorldString) {
        try {
          const waWorld = JSON.parse(waWorldString);
          waWorldId = waWorld.id;
        } catch (parseError) {
          debug.error(config, 'WaItem.getData - Erreur lors du parsing de wa_world:', parseError.message);
        }
      }
      
      if (id === waWorldId) {
        endpoint = '/world';
        granularity = 1;
        this.mainClass = MAIN_CLASS.ARTICLE;
        this.isProtected = true;
      } else {
        // Selon le Main Class, construire l'appel approprié
        switch (mainClass) {
          case MAIN_CLASS.MANUSCRIPT_PART:
            endpoint = '/manuscript_part';
            granularity = 2;
            this.mainClass = mainClass;
            break;
          
          case MAIN_CLASS.ARTICLE:
            endpoint = '/article';
            granularity = 2;
            this.mainClass = mainClass;
            break;
          
          case MAIN_CLASS.BLOCK:
            endpoint = '/block';
            granularity = 2;
            this.mainClass = mainClass;
            break;
          
          default: {
            const errorMsg = `WaItem.getData: Main Class non reconnu pour le chargement des données: ${mainClass}`;
            debug.error(config, errorMsg);
            throw new Error(errorMsg);
          }
        }
      }

      // Appeler _waUpdateObjectWithData avec les paramètres déterminés
      return await this._waUpdateObjectWithData(id, this.mainClass, endpoint, granularity);

    } catch (error) {
      debug.error(config, 'WaItem.getData - Erreur lors du chargement des données:', error.message, { id, mainClass, role: this.role });
      this.id = ERROR_ID;
      this.data = { ...ERROR_DATA };
      throw error;
    }
  }

  // Fonction refreshData spéciale pour le waItem Main
  async refreshData() {
    if (!this.isMain) {
      const errorMsg = 'refreshData() est réservé au waItem Main';
      debug.error(config, 'WaItem.refreshData:', errorMsg);
      throw new Error(errorMsg);
    }

    try {
      // Le waItem Main a toujours ManuscriptPart comme Main Class
      this.mainClass = MAIN_CLASS.MANUSCRIPT_PART;

      // Récupérer l'ID de version depuis le localStorage
      const waVersionString = localStorage.getItem('wa_version');
      if (!waVersionString) {
        const errorMsg = 'wa_version non trouvé dans le localStorage';
        debug.error(config, 'WaItem.refreshData:', errorMsg);
        throw new Error(errorMsg);
      }
      
      let waVersion;
      try {
        waVersion = JSON.parse(waVersionString);
      } catch (parseError) {
        const errorMsg = 'Erreur lors du parsing de wa_version depuis le localStorage';
        debug.error(config, 'WaItem.refreshData:', errorMsg, parseError);
        throw new Error(errorMsg);
      }
      
      const waVersionId = waVersion.id;
      if (!waVersionId) {
        const errorMsg = 'wa_version.id non trouvé dans le localStorage';
        debug.error(config, 'WaItem.refreshData:', errorMsg);
        throw new Error(errorMsg);
      }

      // Première requête : obtenir la version du manuscript
      const versionData = await waproxyClient.waGet('/manuscript_version', waVersionId, 2);
      const activePartId = versionData.activePart?.id;

      if (!activePartId) {
        const errorMsg = 'activePart.id non trouvé dans la réponse de manuscript_version';
        debug.error(config, 'WaItem.refreshData:', errorMsg, { versionData });
        throw new Error(errorMsg);
      }

      // Appeler getData en interne avec l'ID de la partie active
      await this._waUpdateObjectWithData(activePartId, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);

      return this.data;

    } catch (error) {
      debug.error(config, 'WaItem.refreshData - Erreur lors du refresh des données waItem Main:', error.message, { waVersion: localStorage.getItem('wa_version') });
      this.id = ERROR_ID;
      this.data = { ...ERROR_DATA };
      throw error;
    }
  }

  // Fonction pour traiter les données d'un item (métadonnées, tags, enrichissement)
  async _waReadData(id, mainClass, endpoint, granularity) {
    // Récupération des données depuis l'API
    const responseData = await waproxyClient.waGet(endpoint, id, granularity);
    
    // NOTA: L'enrichissement des données (parsing userMetadata, block YAML, etc.)
    // est désormais effectué par le worker wab-waproxy dans enrichWorldAnvilData()
    // Le frontend reçoit directement les données enrichies

    // Lecture des tags
    // Initialiser les listes
    const belTagNames = [];
    const belTags = [];
    const otherTagNames = [];

    // Récupérer bel_tags depuis le localStorage
    let belTagsConfig = {};
    try {
      const belTagsString = localStorage.getItem('bel_tags');
      belTagsConfig = belTagsString ? JSON.parse(belTagsString) : {};
    } catch (parseError) {
      debug.error(config, 'WaItem._waReadData - Erreur lors du parsing de bel_tags:', parseError.message);
    }

    if (responseData.tags && Array.isArray(responseData.tags)) {
      // Parcourir les tags
      responseData.tags.forEach(tag => {
        if (tag.id && belTagsConfig[tag.id]) {
          // Tag trouvé dans bel_tags
          belTagNames.push(belTagsConfig[tag.id].display_label);
          belTags.push(belTagsConfig[tag.id].config_tag_key);
        } else if (tag.title) {
          // Tag non trouvé dans bel_tags
          otherTagNames.push(tag.title);
        }
      });
     }

     // Si belTags est vide et qu'il s'agit d'un ManuscriptPart
     if (belTags.length === 0 && mainClass === MAIN_CLASS.MANUSCRIPT_PART) {
       const defaultTag = belTagsConfig['00000000-0000-0000-0000-000000000001'];
       if (defaultTag) {
         belTagNames.push(defaultTag.display_label);
         belTags.push(defaultTag.config_tag_key);
         debug.log(config, ' Aucun bel_tag trouvé, ajout du tag par défaut pour ManuscriptPart');
       } else {
         debug.warn(config, 'WaItem._waReadData: Tag par défaut 00000000-0000-0000-0000-000000000001 non trouvé dans bel_tags');
       }
     } 

    // Récupérer la liste des clés parsées depuis le worker (si présent)
    const blockKeys = responseData.blockKeys || [];

    // Enrichir les données avec le Main Class et les tags
    const enrichedData = {
      _mainClass: mainClass,
      _class: mainClass === 'ManuscriptPart' ? responseData.type : responseData.entityClass,
      _tags: belTags,
      ...responseData,
    };

    return {
      enrichedData,
      belTags,
      belTagNames,
      otherTagNames,
      blockKeys
    };
  }

  // Fonction interne getData pour le waItem Main
  async _waUpdateObjectWithData(id, mainClass, endpoint = '/manuscript_part', granularity = 2) {
    try {
      this.id = id;
      this.mainClass = mainClass;

      // Traitement des données via la fonction dédiée
      const result = await this._waReadData(id, mainClass, endpoint, granularity);
      
      // Mettre à jour les propriétés de l'instance
      this.belTagNames = result.belTagNames;
      this.belTags = result.belTags;
      this.otherTagNames = result.otherTagNames;
      this.blockKeys = result.blockKeys;

      // Stocker les données
      this.data = result.enrichedData;

      return this.data;

    } catch (error) {
      debug.error(config, 'WaItem._waUpdateObjectWithData - Erreur lors du chargement interne des données waItem Main:', error.message, { id, mainClass });
      this.id = ERROR_ID;
      this.data = { ...ERROR_DATA };
      throw error;
    }
  }

  // Réinitialiser les données des axes (cache des parts connexes)
  clearAxisData() {
    this.axisData = {};
    debug.log(config, 'WaItem.clearAxisData: Cache des axes réinitialisé');
  }

  // Getter pour vérifier si les données sont chargées
  get isLoaded() {
    return this.data !== null && this.id !== ERROR_ID;
  }


  /* *
   * Fonction pour charger les données des parts connexes
   * Ne fonctionne que si mainClass est ManuscriptPart
   * Les données des parts sont stockées dans l'objet
   */

  // Fonction privée pour charger les données des enfants
  async _readChildrenPartData() {
    const axisKey = PATH_AXIS.CHILDREN + 'Part';
    
    // Vérifier que les données sont chargées et qu'il y a des enfants
    if (!this.data.children || !Array.isArray(this.data.children)) {
      debug.log(config, `WaItem._readChildrenPartData: Aucun enfant à charger`);
      return [];
    }

    // Ne pas charger les données si elles sont déjà présentes
    if (this.axisData[axisKey]) {
      debug.log(config, `WaItem._readChildrenPartData: Données des enfants déjà chargées, pas de rechargement`);
      return this.axisData[axisKey];
    }

    // Réinitialiser le tableau des données des enfants
    this.axisData[axisKey] = [];

    // Traiter chaque enfant
    for (const child of this.data.children) {
      if (child.id) {
        try {
          // Récupérer les données enrichies de l'enfant
          const childResult = await this._waReadData(child.id, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);

          // Ajouter les données enrichies à axisData
          this.axisData[axisKey].push(childResult.enrichedData);

          debug.log(config, `WaItem._readChildrenPartData: Enfant chargé avec succès`, { 
            childId: child.id,
            title: childResult.enrichedData.title 
          });

        } catch (childError) {
          debug.error(config, `WaItem._readChildrenPartData: Erreur lors du chargement d'un enfant`, 
            childError.message, { childId: child.id });
          // Continuer avec les autres enfants même si un échoue
        }
      } else {
        debug.warn(config, `WaItem._readChildrenPartData: Enfant sans ID trouvé`, { child });
      }
    }

    debug.log(config, `WaItem._readChildrenPartData: Chargement terminé`, this.axisData[axisKey].length );

    return this.axisData[axisKey];
  }

  // Fonction privée pour charger les données du parent
  async _readParentPartData() {
    const axisKey = PATH_AXIS.PARENT + 'Part';
    
    // Vérifier si les données sont déjà chargées (non-null = déjà traité)
    if (this.axisData[axisKey] !== undefined) {
      debug.log(config, 'WaItem._readParentPartData: Données déjà chargées, pas de rechargement');
      return this.axisData[axisKey];
    }
    
    // Initialiser à null (pas de parent trouvé)
    this.axisData[axisKey] = null;
    
    // Vérifier que this.data.parent.id n'est pas vide ou null
    if (!this.data.parent || !this.data.parent.id) {
      debug.log(config, 'WaItem._readParentPartData: Aucun parent trouvé');
      return this.axisData[axisKey];
    }
    
    // Récupérer les données du parent
    const parentResult = await this._waReadData(this.data.parent.id, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);
    this.axisData[axisKey] = parentResult.enrichedData;
    
    debug.log(config, 'WaItem._readParentPartData: Parent trouvé:', this.axisData[axisKey].id);
    return this.axisData[axisKey];
  }

  // Fonction privée pour charger les données de la partie précédente
  async _readPreviousPartData(family) {
    const axisKey = PATH_AXIS.PREVIOUS + 'Part';
    let belTagsData = {};
    try {
      belTagsData = JSON.parse(localStorage.getItem('bel_tags') || '{}');
    } catch (parseError) {
      debug.error(config, 'WaItem._readPreviousPartData - Erreur parsing bel_tags:', parseError.message);
    }
    
    // Ne pas charger les données si elles sont déjà présentes (non-undefined = déjà traité)
    if (this.axisData[axisKey] !== undefined) {
      debug.log(config, 'WaItem._readPreviousPartData: Données déjà chargées, pas de rechargement');
      return this.axisData[axisKey];
    }
    
    // Initialiser à null (pas de previous trouvé)
    this.axisData[axisKey] = null;
    
    // Récupérer les données du parent via _readParentPartData
    const responseParent = await this._readParentPartData();
    
    // Vérifier si on a bien des données de parent
    if (!responseParent) {
      return this.axisData[axisKey];
    }
    
    // Construire la liste des children du parent
    const children = responseParent.children || [];
    const mainItemIndex = children.findIndex(child => child.id === this.id);
    
    if (mainItemIndex > 0) {
      // Parcourir les children du parent vers le début
      for (let i = mainItemIndex - 1; i >= 0; i--) {
        const childId = children[i].id;
        
        // _waReadData pour récupérer les données avec granularity 2
        const response = await this._waReadData(childId, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);
        
        // Vérifier les tags pour trouver un isBeat
        if (response.enrichedData.tags) {
          for (const tag of response.enrichedData.tags) {
            // Récupérer les détails du tag depuis localStorage
            const tagDetails = belTagsData[tag.id];
            
            if (tagDetails && (family === null || tagDetails.config_tag_key === family)) {
              this.axisData[axisKey] = response.enrichedData;
              break;
            }
          }
        }
        
        // Si on a trouvé un élément avec la family, arrêter la recherche
        if (this.axisData[axisKey] !== null) {
          break;
        }
      }
    }
    
    debug.log(config, 'WaItem._readPreviousPartData: PreviousPartData trouvé:', this.axisData[axisKey]?.id || 'aucun');
    return this.axisData[axisKey];
  }

  // Fonction privée pour charger les données des siblings précédents
  async _readPrecedingSiblingPartData() {
    const axisKey = PATH_AXIS.PRECEDING_SIBLING + 'Part';
    
    // Vérifier si les données sont déjà chargées
    if (this.axisData[axisKey]) {
      debug.log(config, 'WaItem._readPrecedingSiblingPartData: Données déjà chargées, pas de rechargement');
      return this.axisData[axisKey];
    }
    
    // Initialiser avec un tableau vide
    this.axisData[axisKey] = [];
    
    // Récupérer les données du parent via _readParentPartData
    const responseParent = await this._readParentPartData();
    
    // Vérifier si on a bien des données de parent
    if (!responseParent) {
      return this.axisData[axisKey];
    }
    
    // Construire la liste des children du parent
    const children = responseParent.children || [];
    const mainItemIndex = children.findIndex(child => child.id === this.id);
    
    if (mainItemIndex > 0) {
      // Récupérer tous les children avant l'index courant
      for (let i = 0; i < mainItemIndex; i++) {
        const childId = children[i].id;
        
        // _waReadData pour récupérer les données avec granularity 2
        const response = await this._waReadData(childId, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);
        this.axisData[axisKey].push(response.enrichedData);
      }
    }
    
    debug.log(config, 'WaItem._readPrecedingSiblingPartData: Siblings précédents trouvés:', this.axisData[axisKey].length);
    return this.axisData[axisKey];
  }

  // Fonction privée pour charger les données des siblings suivants
  async _readFollowingSiblingPartData() {
    const axisKey = PATH_AXIS.FOLLOWING_SIBLING + 'Part';
    
    // Vérifier si les données sont déjà chargées
    if (this.axisData[axisKey]) {
      debug.log(config, 'WaItem._readFollowingSiblingPartData: Données déjà chargées, pas de rechargement');
      return this.axisData[axisKey];
    }
    
    // Initialiser avec un tableau vide
    this.axisData[axisKey] = [];
    
    // Récupérer les données du parent via _readParentPartData
    const responseParent = await this._readParentPartData();
    
    // Vérifier si on a bien des données de parent
    if (!responseParent) {
      return this.axisData[axisKey];
    }
    
    // Construire la liste des children du parent
    const children = responseParent.children || [];
    const mainItemIndex = children.findIndex(child => child.id === this.id);
    
    if (mainItemIndex >= 0 && mainItemIndex < children.length - 1) {
      // Récupérer tous les children après l'index courant
      for (let i = mainItemIndex + 1; i < children.length; i++) {
        const childId = children[i].id;
        
        // _waReadData pour récupérer les données avec granularity 2
        const response = await this._waReadData(childId, MAIN_CLASS.MANUSCRIPT_PART, '/manuscript_part', 2);
        this.axisData[axisKey].push(response.enrichedData);
      }
    }
    
    debug.log(config, 'WaItem._readFollowingSiblingPartData: Siblings suivants trouvés:', this.axisData[axisKey].length);
    return this.axisData[axisKey];
  }

  // fonction publique pour charger les parts connexes
  async readRelativePartData(relativeType = PATH_AXIS.CHILDREN, family = null) {
    // Vérifier que la fonction n'est utilisée que pour ManuscriptPart
    if (this.mainClass !== MAIN_CLASS.MANUSCRIPT_PART) {
      debug.error(config, `WaItem.readRelativePartData ${relativeType}: readRelativePartData() ne fonctionne que pour mainClass === MAIN_CLASS.MANUSCRIPT_PART`, { mainClass: this.mainClass });
      return;
    }

    // Vérifier que les données sont chargées
    if (!this.isLoaded || !this.data) {
      debug.log(config, `WaItem.readRelativePartData ${relativeType}: Données non chargées, impossible de charger les parts connexes`);
      return;
    }

    try {
      switch (relativeType) {
        case PATH_AXIS.CHILDREN:
          return await this._readChildrenPartData();

        case PATH_AXIS.PARENT:
          return await this._readParentPartData();
        
        case PATH_AXIS.PREVIOUS:
          return await this._readPreviousPartData(family);
        
        case PATH_AXIS.PRECEDING_SIBLING:
          return await this._readPrecedingSiblingPartData();
        
        case PATH_AXIS.FOLLOWING_SIBLING:
          return await this._readFollowingSiblingPartData();
        
        default:
          debug.error(config, `WaItem.readRelativePartData ${relativeType}: Type de relation ${relativeType} non reconnu`);
          return;
      }

    } catch (error) {
      debug.error(config, `WaItem.readRelativePartData ${relativeType}: Erreur générale lors du chargement`, error.message);
    }
  }

  /* *
   * Fonction pour écrire les données du waItem Main
   * Ne fonctionne que si isMain est true et mainClass est ManuscriptPart
  */

  // Méthode patchData pour mettre à jour les données
  async patchData(patchData) {
    if (this.isProtected === true) {
      debug.error(config, 'WaItem.patchData: Impossible de modifier un waItem protégé');
      return;
    }

    await this._waWriteData(this.id, this.mainClass, patchData, this.blockKeys, this.data, false);
  }

  // Méthode interne _waWriteData pour écrire les données
  async _waWriteData(id, mainClass, data, crunchKeys = null, originalCrunchSet = null, create = false) {
    let endpoint;
    let datatowrite;

    switch (mainClass) {
      case MAIN_CLASS.MANUSCRIPT_PART:
        endpoint = '/manuscript_part';
        datatowrite = data;
        break;
        
      case MAIN_CLASS.ARTICLE:
        endpoint = '/article';
        datatowrite = data;
        break;
      
      case MAIN_CLASS.BLOCK:
        endpoint = '/block';
        datatowrite = { ...data };
        
        if (!crunchKeys || crunchKeys.length === 0) {
          debug.error(config, 'WaItem._waWriteData: crunchKeys est null ou vide pour MAIN_CLASS.BLOCK');
          return;
        }

        // Construire theNewBlock avec les valeurs de originalCrunchSet et data
        const theNewBlock = {};
        
        crunchKeys.forEach(key => {
          // D'abord prendre les valeurs de originalCrunchSet si elles existent
          if (originalCrunchSet && originalCrunchSet.hasOwnProperty(key)) {
            theNewBlock[key] = originalCrunchSet[key];
          }
          
          // Puis remplacer par les valeurs de data si elles existent
          if (data.hasOwnProperty(key)) {
            theNewBlock[key] = data[key];
            // Enlever la clé de datatowrite
            delete datatowrite[key];
          }
        });

        // Ajouter textualdata avec le YAML stringifié
        datatowrite.textualdata = YAML.stringify(theNewBlock);
        break;
      
      default:
        debug.error(config, `WaItem._waWriteData: Main Class non reconnu pour write data : ${mainClass}`);
        return;
    }

    if (create === false) {
      debug.log(config, 'WaItem._waWriteData: Mise à jour des données via PATCH', { endpoint, id, datatowrite });
      await waproxyClient.waPatch(endpoint, id, datatowrite);
    } else {
      // TODO: Implémenter la création
      debug.log(config, 'WaItem._waWriteData: Création non encore implémentée');
    }
  }

}